/**
 * HelpDrawer — tiroir d'aide contextuel (orchestrateur). Un CHAMP UNIFIÉ réunit
 * recherche d'articles et assistant IA, en vue liste ET en tête d'article (§3).
 * Modes `overlay` (Sheet) / `docked` (<aside>). Slot consume-once §8 (palette).
 */

import { useTranslation } from 'react-i18next'

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet'
import { cn } from '../../lib/utils'
import { type HelpArticle, type HelpDrawerMode } from '../../hooks/useHelp'
import type { PendingHelpIntent } from '../../contexts/HelpContext'
import { HelpDrawerHeader } from './HelpDrawerHeader'
import { HelpDrawerFooter } from './HelpDrawerFooter'
import { HelpDrawerBody } from './HelpDrawerBody'
import { useHelpDrawerState } from './useHelpDrawerState'

export interface HelpDrawerProps {
    isOpen: boolean
    onClose: () => void
    articles: HelpArticle[]
    currentArticle: HelpArticle | null
    onShowArticle: (slug: string) => void
    onBackToList: () => void
    isLoading?: boolean
    error?: string | null
    mode?: HelpDrawerMode
    isDocked?: boolean
    onToggleMode?: () => void
    /** Active l'assistant IA (vrai flag `HELP_ASSISTANT`). FALSE sur pages publiques. */
    enableAssistant?: boolean
    /** Slot consume-once déposé par la passerelle (§8). */
    pendingIntent?: PendingHelpIntent | null
    onConsumeIntent?: () => void
    /** Identifiant utilisateur (clé d'historique localStorage tenant+user). */
    userId?: string
}

export function HelpDrawer({
    isOpen,
    onClose,
    articles,
    currentArticle,
    onShowArticle,
    onBackToList,
    isLoading = false,
    error = null,
    mode = 'overlay',
    isDocked = false,
    onToggleMode,
    enableAssistant = false,
    pendingIntent = null,
    onConsumeIntent,
    userId,
}: HelpDrawerProps) {
    const { t } = useTranslation()

    const {
        assistant,
        inAssistantThread,
        pendingSection,
        handleAsk,
        handleSelectArticle,
        handleNewConversation,
        headerBack,
        headerTitle,
        handleOpenDocCenter,
        searchProps,
    } = useHelpDrawerState({
        isOpen,
        articles,
        currentArticle,
        onShowArticle,
        onBackToList,
        isLoading,
        error,
        enableAssistant,
        pendingIntent,
        onConsumeIntent,
        userId,
    })

    const panelInner = (
        <>
            <HelpDrawerHeader
                title={headerTitle}
                description={!currentArticle && !inAssistantThread ? t('help.description') : null}
                back={headerBack}
                showBeta={inAssistantThread}
                showPinned={!!currentArticle && isDocked}
                showNewConversation={
                    inAssistantThread && assistant.messages.length > 0 && !assistant.atLimit
                }
                onNewConversation={handleNewConversation}
                mode={mode}
                onToggleMode={onToggleMode}
                onClose={onClose}
            />

            <HelpDrawerBody
                inAssistantThread={inAssistantThread}
                assistant={assistant}
                onAsk={handleAsk}
                onReset={handleNewConversation}
                onSelectArticle={handleSelectArticle}
                isLoading={isLoading}
                error={error}
                currentArticle={currentArticle}
                pendingSection={pendingSection}
                searchProps={searchProps}
            />

            <HelpDrawerFooter
                isReadingArticle={!!currentArticle}
                onOpenDocCenter={handleOpenDocCenter}
            />
        </>
    )

    // Mode ancré (desktop) : panneau latéral NON-modal (sans focus-trap).
    if (isDocked) {
        return (
            <aside
                role="complementary"
                aria-label={t('help.title')}
                className={cn(
                    'fixed right-0 top-0 z-40 flex h-full w-full flex-col lg:w-[32rem]',
                    'border-l border-border bg-background shadow-md',
                    'animate-in slide-in-from-right duration-300 ease-out motion-reduce:animate-none',
                )}
            >
                {panelInner}
            </aside>
        )
    }

    // Mode overlay (défaut + mobile) : modale Radix Sheet.
    return (
        <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <SheetContent
                side="right"
                className="flex w-full flex-col gap-0 bg-background p-0 sm:max-w-lg [&>button]:hidden"
            >
                <SheetHeader className="sr-only">
                    <SheetTitle>{headerTitle}</SheetTitle>
                    <SheetDescription>{t('help.description')}</SheetDescription>
                </SheetHeader>
                {panelInner}
            </SheetContent>
        </Sheet>
    )
}

export default HelpDrawer
